import { useState, useCallback, useEffect, useMemo } from "react";

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface MarqueeField {
  id: string;
  rect: Rect;
}

interface UseMarqueeSelectionOptions {
  canvasRef: React.RefObject<HTMLDivElement>;
  scale: number;
  getCanvasCoords: (e: React.MouseEvent | MouseEvent) => { x: number; y: number };
  getCurrentPageFields: () => MarqueeField[];
}

interface UseMarqueeSelectionResult {
  selectedFieldIds: Set<string>;
  setSelectedFieldIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  marqueePreview: { left: number; top: number; width: number; height: number } | null;
  /** Call from container onMouseDown when in "select" mode. Returns true if marquee started. */
  startMarquee: (e: React.MouseEvent) => boolean;
  /** Handle click on a field (supports shift-click toggle). */
  handleFieldClick: (fieldId: string, shiftKey: boolean) => void;
  /** Select all fields on current page. */
  selectAll: () => void;
  /** Clear selection. */
  clearSelection: () => void;
}

function rectsOverlap(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

export function useMarqueeSelection({
  canvasRef,
  scale,
  getCanvasCoords,
  getCurrentPageFields,
}: UseMarqueeSelectionOptions): UseMarqueeSelectionResult {
  const [selectedFieldIds, setSelectedFieldIds] = useState<Set<string>>(new Set());
  const [marqueeStart, setMarqueeStart] = useState<{ x: number; y: number } | null>(null);
  const [marqueeCurrent, setMarqueeCurrent] = useState<{ x: number; y: number } | null>(null);

  const startMarquee = useCallback(
    (e: React.MouseEvent): boolean => {
      // Don't start marquee when clicking on draggable fields
      const target = e.target as HTMLElement;
      if (target.closest(".react-draggable")) return false;

      e.preventDefault();
      const coords = getCanvasCoords(e);
      setMarqueeStart(coords);
      setMarqueeCurrent(coords);
      return true;
    },
    [getCanvasCoords],
  );

  // Window-level listeners for marquee so it works even when mouse leaves the canvas/container
  useEffect(() => {
    if (!marqueeStart) return;

    const onMouseMove = (e: MouseEvent) => {
      if (!canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      setMarqueeCurrent({
        x: (e.clientX - rect.left) / scale,
        y: (e.clientY - rect.top) / scale,
      });
    };

    const onMouseUp = (e: MouseEvent) => {
      if (!marqueeCurrent) {
        if (!e.shiftKey) setSelectedFieldIds(new Set());
        setMarqueeStart(null);
        return;
      }

      const marqueeRect: Rect = {
        x: Math.min(marqueeStart.x, marqueeCurrent.x),
        y: Math.min(marqueeStart.y, marqueeCurrent.y),
        width: Math.abs(marqueeCurrent.x - marqueeStart.x),
        height: Math.abs(marqueeCurrent.y - marqueeStart.y),
      };

      const MIN_MARQUEE = 5;
      if (marqueeRect.width > MIN_MARQUEE || marqueeRect.height > MIN_MARQUEE) {
        const fields = getCurrentPageFields();
        const intersecting = fields.filter((f) => rectsOverlap(f.rect, marqueeRect));
        if (e.shiftKey) {
          setSelectedFieldIds((prev) => {
            const next = new Set(prev);
            intersecting.forEach((f) => next.add(f.id));
            return next;
          });
        } else {
          setSelectedFieldIds(new Set(intersecting.map((f) => f.id)));
        }
      } else if (!e.shiftKey) {
        // Click or tiny drag with no fields — clear selection
        setSelectedFieldIds(new Set());
      }

      setMarqueeStart(null);
      setMarqueeCurrent(null);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [marqueeStart, marqueeCurrent, canvasRef, scale, getCurrentPageFields]);

  const marqueePreview = useMemo(
    () =>
      marqueeStart && marqueeCurrent
        ? {
            left: Math.min(marqueeStart.x, marqueeCurrent.x) * scale,
            top: Math.min(marqueeStart.y, marqueeCurrent.y) * scale,
            width: Math.abs(marqueeCurrent.x - marqueeStart.x) * scale,
            height: Math.abs(marqueeCurrent.y - marqueeStart.y) * scale,
          }
        : null,
    [marqueeStart, marqueeCurrent, scale],
  );

  const handleFieldClick = useCallback(
    (fieldId: string, shiftKey: boolean) => {
      if (shiftKey) {
        setSelectedFieldIds((prev) => {
          const next = new Set(prev);
          if (next.has(fieldId)) {
            next.delete(fieldId);
          } else {
            next.add(fieldId);
          }
          return next;
        });
      } else {
        setSelectedFieldIds(new Set([fieldId]));
      }
    },
    [],
  );

  const selectAll = useCallback(() => {
    const fields = getCurrentPageFields();
    setSelectedFieldIds(new Set(fields.map((f) => f.id)));
  }, [getCurrentPageFields]);

  const clearSelection = useCallback(() => {
    setSelectedFieldIds(new Set());
  }, []);

  return {
    selectedFieldIds,
    setSelectedFieldIds,
    marqueePreview,
    startMarquee,
    handleFieldClick,
    selectAll,
    clearSelection,
  };
}
